import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/common/StatusBadge';
import {
  CreditCard,
  Search,
  Download,
  CheckCircle2,
  Clock,
  RotateCcw,
  ExternalLink
} from 'lucide-react';

const PaymentsWorkspace = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGateway, setActiveGateway] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}api/admin/get-orders`, { withCredentials: true });
      const orderList = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.orders)
        ? res.data.orders
        : [];
      setOrders(orderList);
    } catch (err) {
      console.error('Error fetching payments:', err);
      toast.error('Failed to load payments data');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for consistent schema mapping across payment ledger
  const getOrderAmount = (ord) => {
    return Number(ord.pricing?.totalAmount ?? ord.payment?.amount ?? ord.financials?.netTotal ?? ord.totalAmount ?? 0);
  };

  const getPaymentStatus = (ord) => {
    const rawPaymentStatus = (ord.payment?.status || ord.paymentStatus || '').toLowerCase();
    const orderStatus = (ord.status || ord.orderStatus || '').toLowerCase();

    if (['completed', 'paid', 'captured'].includes(rawPaymentStatus)) {
      return 'Paid';
    }
    if (rawPaymentStatus === 'refunded' || orderStatus === 'refunded') {
      return 'Refunded';
    }
    if (rawPaymentStatus === 'failed') {
      return 'Failed';
    }
    if (orderStatus === 'cancelled' || orderStatus === 'canceled') {
      return 'Cancelled';
    }
    if (['placed', 'confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'completed'].includes(orderStatus)) {
      return 'Paid';
    }
    return 'Pending';
  };

  const getGateway = (ord) => {
    return ord.payment?.method || ord.paymentMethod || 'Stripe';
  };

  const getTransactionId = (ord) => {
    return (
      ord.payment?.transactionId ||
      ord.paymentId ||
      ord.stripeSessionId ||
      ord.paypalOrderId ||
      ord.transactionId ||
      ord.paymentDetails?.paymentId ||
      'N/A'
    );
  };

  const getCustomerName = (ord) => {
    if (ord.shippingAddress?.fullName) return ord.shippingAddress.fullName;
    if (ord.shippingAddress?.firstName) {
      return `${ord.shippingAddress.firstName} ${ord.shippingAddress.lastName || ''}`.trim();
    }
    if (ord.customer?.fullName) return ord.customer.fullName;
    if (Array.isArray(ord.address) && ord.address[0]?.fullName) {
      return ord.address[0].fullName;
    }
    if (ord.user?.name) return ord.user.name;
    return 'Customer';
  };

  const getCustomerEmail = (ord) => {
    return (
      ord.email ||
      ord.shippingAddress?.email ||
      ord.customer?.email ||
      ord.user?.email ||
      (Array.isArray(ord.address) && ord.address[0]?.email) ||
      ''
    );
  };

  // Metrics
  const metrics = useMemo(() => {
    let captured = 0;
    let pending = 0;
    let refunded = 0;
    const gateways = {};

    orders.forEach((ord) => {
      const amount = getOrderAmount(ord);
      const status = getPaymentStatus(ord);
      const gw = getGateway(ord);

      gateways[gw] = (gateways[gw] || 0) + amount;

      if (status === 'Paid') {
        captured += amount;
      } else if (status === 'Pending') {
        pending += amount;
      } else if (status === 'Refunded') {
        refunded += amount;
      }
    });

    return { captured, pending, refunded, gateways };
  }, [orders]);

  // Filtered list
  const filteredOrders = useMemo(() => {
    return orders.filter((ord) => {
      if (activeGateway !== 'all') {
        const gw = getGateway(ord).toLowerCase();
        if (activeGateway === 'cod' || activeGateway === 'cash on delivery') {
          if (!gw.includes('cod') && !gw.includes('cash')) return false;
        } else if (!gw.includes(activeGateway.toLowerCase())) {
          return false;
        }
      }

      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const id = ord._id?.toLowerCase() || '';
      const orderNum = (ord.orderNumber || '').toLowerCase();
      const name = getCustomerName(ord).toLowerCase();
      const email = getCustomerEmail(ord).toLowerCase();
      const txId = getTransactionId(ord).toLowerCase();

      return id.includes(q) || orderNum.includes(q) || name.includes(q) || email.includes(q) || txId.includes(q);
    });
  }, [orders, activeGateway, searchQuery]);

  const handleExportCSV = () => {
    const headers = ['Order Ref', 'Date', 'Customer', 'Email', 'Gateway', 'Transaction ID', 'Payment Status', 'Amount (£)'];
    const rows = filteredOrders.map((ord) => [
      `"${ord.orderNumber || ord._id}"`,
      new Date(ord.createdAt).toISOString().slice(0, 10),
      `"${getCustomerName(ord)}"`,
      `"${getCustomerEmail(ord)}"`,
      getGateway(ord),
      `"${getTransactionId(ord)}"`,
      getPaymentStatus(ord),
      getOrderAmount(ord).toFixed(2)
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `payments_reconciliation_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Payments & Settlement Reconciliation</h1>
            <span className="text-xs bg-stone-100 text-stone-600 font-mono px-2 py-0.5 rounded-full font-medium">
              {orders.length} Transactions
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Gateway transaction ledger, captured funds audit, refund deductions, and payment status verification.
          </p>
        </div>

        <button
          onClick={handleExportCSV}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-stone-300 text-stone-700 text-xs font-medium hover:bg-stone-50 transition-colors shadow-xs"
        >
          <Download className="w-3.5 h-3.5" />
          Export Ledger
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Settled & Captured</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-bold text-stone-900 mt-1 font-mono">
            £{metrics.captured.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">Confirmed successful payments</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Pending / In-Flight</span>
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-2xl font-bold text-amber-600 mt-1 font-mono">
            £{metrics.pending.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">Pending gateway authorization</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Refunded Capital</span>
            <RotateCcw className="w-4 h-4 text-rose-600" />
          </div>
          <div className="text-2xl font-bold text-rose-600 mt-1 font-mono">
            £{metrics.refunded.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">Returned to purchasers</div>
        </div>
      </div>

      {/* Main Ledger Table */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        {/* Controls */}
        <div className="p-4 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-1 overflow-x-auto">
            {['all', 'stripe', 'paypal', 'cash on delivery'].map((gw) => (
              <button
                key={gw}
                onClick={() => setActiveGateway(gw)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                  activeGateway === gw
                    ? 'bg-stone-900 text-white shadow-xs'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                }`}
              >
                {gw}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search order, customer, tx ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center text-stone-400">
            <div className="w-6 h-6 border-2 border-stone-300 border-t-amber-600 rounded-full animate-spin mb-3" />
            <span className="text-xs">Loading reconciliation ledger...</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="py-16 text-center text-stone-400">
            <CreditCard className="w-8 h-8 mx-auto stroke-[1.5] mb-2 opacity-50" />
            <div className="text-sm font-medium text-stone-700">No transactions found</div>
            <div className="text-xs text-stone-500 mt-0.5">Try altering the gateway filter or search query.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-stone-50/80 text-stone-500 border-b border-stone-200">
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Order Ref</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Customer</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Gateway</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Tx ID / Intent</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Payment Status</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">Amount (£)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredOrders.map((ord) => {
                  const txId = getTransactionId(ord);
                  const amount = getOrderAmount(ord);
                  const paymentStatus = getPaymentStatus(ord);
                  const gateway = getGateway(ord);
                  const customerName = getCustomerName(ord);
                  const customerEmail = getCustomerEmail(ord);
                  const displayOrderRef = ord.orderNumber || `#${ord._id.slice(-6).toUpperCase()}`;

                  return (
                    <tr key={ord._id} className="hover:bg-amber-50/40 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-stone-900">
                        <Link
                          to={`/order/${ord._id}`}
                          className="hover:text-amber-700 inline-flex items-center gap-1"
                        >
                          {displayOrderRef}
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </Link>
                      </td>
                      <td className="py-3.5 px-4 text-stone-500">
                        {new Date(ord.createdAt).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-medium text-stone-900">
                          {customerName}
                        </div>
                        {customerEmail && (
                          <div className="text-[11px] text-stone-500">
                            {customerEmail}
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-4 font-medium text-stone-700">
                        {gateway}
                      </td>
                      <td className="py-3.5 px-4 font-mono text-[11px] text-stone-500 max-w-[160px] truncate" title={txId}>
                        {txId}
                      </td>
                      <td className="py-3.5 px-4">
                        <StatusBadge status={paymentStatus} />
                      </td>
                      <td className="py-3.5 px-4 text-right font-mono font-bold text-stone-900">
                        £{amount.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsWorkspace;
